import { FC, useContext } from 'react'
import { Box } from '@mui/material'
import { useKeycloak } from '@react-keycloak/web'
import { Navigate, useLocation, useRoutes } from 'react-router-dom'
import EmittersProvider from '../contexts/emitters.context'
import Header from '../components/features/Header'
import Login from '../pages/login'
import Playbook from '../pages/playbook'
import Map from '../pages/map'
import PlaybooksDashboard from '../pages/PlaybooksDashboard'
import RequestFormPage from '../pages/ExerciseRequestForm'
import SpectrumManagerDashboard from '../pages/SpectrumManagerDashboard'
import UserManagementPage from '../pages/UserManagement'
import Requests from '../components/features/SpectrumManagerDashboard/Requests'
import ErrorPage from '../components/shared/ErrorPage'
import { Devices } from '../components/features/SpectrumManagerDashboard/Devices'
import { Licenses } from '../components/features/SpectrumManagerDashboard/Licenses'
import { Platforms } from '../components/features/SpectrumManagerDashboard/Platforms'
import { Exercises } from '../components/features/SpectrumManagerDashboard/Exercises'
import { AuthContext } from '../contexts/auth.context'
import { hasPermission } from '../utils/functions'
import ArcDiagramIframe from '../components/features/SpectrumManagerDashboard/ArcDiagrams'
import { LocationTemplates } from '../components/features/SpectrumManagerDashboard/LocationTemplates'
import AdssDashboardList from '../pages/AdssDashboardList'
import AdssDashboard from '../pages/AdssDashboard'
import { IRole } from '../../shared/types/api/user.type'

interface IProps {
  children: JSX.Element
}

const Layout: FC<IProps> = ({ children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
    <Header />
    <Box sx={{ paddingTop: '50px' }}>{children}</Box>
  </Box>
)

export const spectrumDashboardChildren = [
  { path: 'requests', action: 'read', resource_type: 'requests', element: <Requests /> },
  { path: 'devices', action: 'read', resource_type: 'devices', element: <Devices /> },
  { path: 'licenses', action: 'read', resource_type: 'approvals', element: <Licenses /> },
  { path: 'exercises', action: 'read', resource_type: 'exercises', element: <Exercises /> },
  { path: 'platforms', action: 'read', resource_type: 'platforms', element: <Platforms /> },
  {
    path: 'locations',
    action: 'read',
    resource_type: 'locations',
    element: <LocationTemplates />,
  },
  {
    path: 'links-diagram',
    action: 'read',
    resource_type: 'approvals',
    element: <ArcDiagramIframe />,
  },
]

export const getFirstAccessibleChildPath = (roles: IRole[]) => {
  const child = spectrumDashboardChildren.find((route) => hasPermission(route.action, route.resource_type, roles))
  return child ? `/spectrum-manager-dashboard/${child.path}` : null
}

export const hasAccessToAnyDashboardChild = (roles: IRole[]) => {
  return spectrumDashboardChildren.some((route) => hasPermission(route.action, route.resource_type, roles))
}

export const getFirstAccessibleRoute = (roles: IRole[], routes: typeof routeElements): string => {
  for (const route of routes) {
    if (route.path === '/login') continue

    if (route.action && route.resource_type && hasPermission(route.action, route.resource_type, roles)) {
      return route.path
    }

    if (route.children) {
      for (const child of route.children) {
        if (child?.action && hasPermission(child.action, child.resource_type, roles)) {
          return `${route.path?.endsWith('/') ? route.path : route.path + '/'}${child.path}`
        }
      }
    }
  }

  return '/no-access'
}

const ProtectedRoute: FC<{
  element?: JSX.Element
  type?: string
  meta?: { isSpectrumDashboard?: boolean }
  action?: string
  resource_type?: string
  redirectToFirstAccessibleChild?: boolean
}> = ({ element, type, meta, action, resource_type, redirectToFirstAccessibleChild }) => {
  const { user } = useContext(AuthContext)
  const location = useLocation()
  const { keycloak } = useKeycloak()

  // Visiting /login. If already signed in, skip the form and send them to
  // somewhere they can actually use; otherwise render the login page itself.
  if (type === 'login') {
    if (keycloak.authenticated) {
      return <Navigate to={getFirstAccessibleRoute(user?.roles || [], routeElements)} />
    } else {
      return element!
    }
  }

  // Landed on an error page, but the user does have at least one read permission
  // (e.g. roles were just granted, or they bookmarked the page). Bounce them
  // to a route they can actually see instead of showing the dead-end message.
  if (element?.type === ErrorPage && user?.roles?.find((role) => role.permissions?.some((p) => p.action === 'read'))) {
    return <Navigate to={getFirstAccessibleRoute(user.roles, routeElements)} />
  }

  // Hitting any non-login route without a Keycloak session — kick to /login.
  if (!keycloak.authenticated) {
    return <Navigate to="/login" />
  }

  let redirectPath = getFirstAccessibleChildPath(user?.roles || [])

  // Index route under a parent that's just a shell (e.g. /spectrum-manager-dashboard
  // with no child segment). Forward to the first child tab the user can see so
  // they don't stare at an empty layout.
  if (redirectToFirstAccessibleChild && redirectPath) {
    return <Navigate to={redirectPath} replace />
  }

  // Route declares an action/resource_type requirement and the user fails it.
  // Send them somewhere they're allowed instead of rendering the page.
  if (action && resource_type && (!user || !hasPermission(action, resource_type, user.roles))) {
    return <Navigate to={getFirstAccessibleRoute(user?.roles || [], routeElements)} />
  }

  // Spectrum-manager-dashboard parent route has no per-route permission of its
  // own; gate it on whether the user can access *any* of its children.
  if (meta?.isSpectrumDashboard && user && !hasAccessToAnyDashboardChild(user?.roles || [])) {
    return <Navigate to={getFirstAccessibleRoute(user.roles, routeElements)} />
  }

  // Authenticated, authorized, and not a redirect-only route — render the page.
  return element!
}

export const routeElements = [
  {
    path: '/map',
    action: 'read',
    resource_type: 'map',
    element: (
      <EmittersProvider>
        <Layout>
          <Map />
        </Layout>
      </EmittersProvider>
    ),
  },
  {
    path: '/playbooks',
    action: 'read',
    resource_type: 'playbooks',
    element: (
      <Layout>
        <PlaybooksDashboard />
      </Layout>
    ),
  },
  {
    path: '/login',
    type: 'login',
    element: <Login />,
  },
  {
    path: '/',
    action: 'write',
    resource_type: 'requests',
    element: <Navigate to="/frequency-request-form" replace />,
  },
  {
    path: '/spectrum-manager-dashboard',
    meta: { isSpectrumDashboard: true },
    element: (
      <ProtectedRoute
        element={
          <Layout>
            <SpectrumManagerDashboard />
          </Layout>
        }
      />
    ),
    children: [
      ...spectrumDashboardChildren.map((child) => ({
        ...child,
        element: <ProtectedRoute action={child.action} resource_type={child.resource_type} element={child.element} />,
      })),
      {
        index: true,
        path: '',
        action: '',
        resource_type: '',
        element: <ProtectedRoute redirectToFirstAccessibleChild />,
      },
    ],
  },
  {
    path: '/frequency-request-form',
    action: 'write',
    resource_types: 'requests',
    element: (
      <Layout>
        <RequestFormPage />
      </Layout>
    ),
  },
  {
    path: '/users-management',
    action: 'read',
    resource_type: 'users',
    element: (
      <Layout>
        <UserManagementPage />
      </Layout>
    ),
  },
  {
    path: '/no-access',
    element: (
      <ProtectedRoute
        element={
          <ErrorPage message="You don't have any permissions to access Spectrum Exchange dashboard. Please contact your managers." />
        }
      />
    ),
  },
  {
    path: '/playbooks/:playbookId',
    action: 'write',
    resource_type: 'playbooks',
    element: (
      <Layout>
        <Playbook />
      </Layout>
    ),
  },
  {
    path: '/dashboards',
    element: (
      <Layout>
        <AdssDashboardList />
      </Layout>
    ),
  },
  {
    path: '/dashboard/:dashboardId',
    element: (
      <Layout>
        <AdssDashboard />
      </Layout>
    ),
  },
]

const RoutesComponent = () => {
  const wrapWithProtection = (route: any) => ({
    ...route,
    element: (
      <ProtectedRoute
        meta={route.meta}
        action={route.action}
        resource_type={route.resource_type}
        type={route.type}
        element={route.element}
      />
    ),
  })

  const protectedRouteElements = routeElements.map((route) => {
    if (route.children) {
      return {
        ...wrapWithProtection(route),
        children: route.children,
      }
    }
    return wrapWithProtection(route)
  })

  const routes = useRoutes(protectedRouteElements)

  return routes
}

export default RoutesComponent
